/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';

import { ApiStatus, LoaderStates } from '@ibm-watson/shared/ts';
import { integrationsAdapter } from '../channels/adapter';
import {
  connectContactCenter,
  connectServiceDeskIntegration,
  loadServiceDeskIntegration,
  resetServiceDesk,
  updateServiceDeskIntegration
} from './actions';

export const initialState = {
  connectServiceDesk: {
    status: ApiStatus.IDLE,
    error: null
  },
  loadServiceDesk: {
    status: ApiStatus.IDLE,
    error: null
  },
  updateServiceDesk: {
    status: ApiStatus.IDLE,
    error: null
  },
  deleteServiceDesk: {
    status: ApiStatus.IDLE,
    error: null
  },
  draftServiceDesk: {
    integration: null,
    name: {
      valid: null,
      dirty: null
    },
    settings: {
      valid: null,
      dirty: null
    }
  }
};

export const reducers = {
  defineNewServiceDesk(state, action) {
    const { integration } = action.payload;

    state.serviceDesk.draftServiceDesk.integration = integration;
    state.serviceDesk.draftServiceDesk.name.valid = true;
    state.serviceDesk.draftServiceDesk.name.dirty = false;
    state.serviceDesk.draftServiceDesk.settings.valid = true;
    state.serviceDesk.draftServiceDesk.settings.dirty = false;
  },
  updateServiceDeskSettings(state, action) {
    const { settings, isValid, isDirty } = action.payload;
    if (state.serviceDesk.draftServiceDesk.integration) {
      // We need to merge the contents of the integration rather than
      // overwrite them.
      state.serviceDesk.draftServiceDesk.integration.settings = _.mergeWith(
        state.serviceDesk.draftServiceDesk.integration.settings,
        settings,
        // Array contents of settings override the ones in the
        // current instance, and they are not merged.
        (objValue, srcValue) => {
          if (_.isArray(objValue)) {
            return srcValue;
          }
        }
      );
    } else {
      state.serviceDesk.draftServiceDesk.integration.settings = settings;
    }

    state.serviceDesk.draftServiceDesk.settings.valid = isValid;
    state.serviceDesk.draftServiceDesk.settings.dirty = isDirty;
  }
};

export const extraReducers = builder => {
  builder
    // LOAD SERVICE DESK
    .addCase(loadServiceDeskIntegration.pending, state => {
      state.serviceDesk.loadServiceDesk.status = ApiStatus.LOADING;
      state.serviceDesk.loadServiceDesk.error = null;
    })
    .addCase(loadServiceDeskIntegration.fulfilled, (state, action) => {
      state.serviceDesk.loadServiceDesk.status = ApiStatus.SUCCEEDED;
      state.serviceDesk.loadServiceDesk.error = null;

      // Unless updateServiceDeskDraft is explicitly false
      if (action.payload?.updateServiceDeskDraft ?? true) {
        state.serviceDesk.draftServiceDesk.integration = action.payload;
      }

      state.serviceDesk.draftServiceDesk.name.valid = true;
      state.serviceDesk.draftServiceDesk.name.dirty = false;
      state.serviceDesk.draftServiceDesk.settings.valid = true;
      state.serviceDesk.draftServiceDesk.settings.dirty = false;
    })
    .addCase(loadServiceDeskIntegration.rejected, (state, action) => {
      state.serviceDesk.loadServiceDesk.status = ApiStatus.ERROR;
      state.serviceDesk.loadServiceDesk.error = action.error;

      state.serviceDesk.draftServiceDesk.integration = null;
    })
    // UPDATE SERVICE DESK
    .addCase(updateServiceDeskIntegration.pending, (state, _) => {
      state.serviceDesk.updateServiceDesk.status = ApiStatus.LOADING;
      state.serviceDesk.updateServiceDesk.error = null;
    })
    .addCase(updateServiceDeskIntegration.fulfilled, (state, action) => {
      state.serviceDesk.updateServiceDesk.status = ApiStatus.SUCCEEDED;
      state.serviceDesk.updateServiceDesk.error = null;

      // state.serviceDesk.draftServiceDesk.integration = action.payload;
      integrationsAdapter.upsertOne(state, action.payload);

      // invalidate listIntegrations cache
      state.listIntegrations.fetched = null;
    })
    .addCase(updateServiceDeskIntegration.rejected, (state, action) => {
      state.serviceDesk.updateServiceDesk.status = ApiStatus.ERROR;
      state.serviceDesk.updateServiceDesk.error = action.error;
    })
    // CONNECT SERVICE DESK
    .addCase(connectServiceDeskIntegration.pending, (state, _) => {
      state.serviceDesk.connectServiceDesk.status = ApiStatus.LOADING;
      state.serviceDesk.connectServiceDesk.error = null;
    })
    .addCase(connectServiceDeskIntegration.fulfilled, (state, _) => {
      state.serviceDesk.connectServiceDesk.status = ApiStatus.SUCCEEDED;
      state.serviceDesk.connectServiceDesk.error = null;
    })
    .addCase(connectServiceDeskIntegration.rejected, (state, action) => {
      state.serviceDesk.connectServiceDesk.status = ApiStatus.ERROR;
      state.serviceDesk.connectServiceDesk.error = action.error;
    })
    // CONNECT CONTACT CENTER
    .addCase(connectContactCenter.pending, (state, _action) => {
      state.phone.contact_center.status = LoaderStates.ACTIVE;
    })
    .addCase(connectContactCenter.fulfilled, (state, _action) => {
      state.phone.contact_center.status = LoaderStates.SUCCESS;
      // allow the Save and Exit button to be enabled
      state.draftIntegration.disable_save_create_button = false;
    })
    .addCase(connectContactCenter.rejected, (state, _action) => {
      state.phone.contact_center.status = LoaderStates.ERROR;
      // disable the Save and Exit button
      state.draftIntegration.disable_save_create_button = true;
    })
    // RESET SERVICE DESK
    .addCase(resetServiceDesk, (state, _action) => {
      // Do not reset if we're in the middle of updating
      if (state.serviceDesk.updateServiceDesk.status === ApiStatus.LOADING) {
        return;
      }

      if (
        !_.isEmpty(
          _.get(
            state,
            'serviceDesk.draftServiceDesk.integration.integration_id'
          )
        )
      ) {
        const serviceDeskId = _.get(
          state,
          'serviceDesk.draftServiceDesk.integration.integration_id'
        );

        if (
          _.get(
            state,
            'draftIntegration.integration.settings.service_desk.integration_id'
          ) === serviceDeskId
        ) {
          state.draftIntegration.integration.settings.service_desk = {
            is_on: false,
            integration_id: '',
            integration_type: ''
          };
          state.draftIntegration.settings.dirty = true;
        }
      }

      state.serviceDesk.draftServiceDesk.integration = null;
      state.serviceDesk.draftServiceDesk.name.valid = null;
      state.serviceDesk.draftServiceDesk.name.dirty = null;
      state.serviceDesk.draftServiceDesk.settings.valid = null;
      state.serviceDesk.draftServiceDesk.settings.dirty = null;
      state.serviceDesk.connectServiceDesk.status = ApiStatus.IDLE;
      state.serviceDesk.loadServiceDesk.status = ApiStatus.IDLE;
      state.serviceDesk.updateServiceDesk.status = ApiStatus.IDLE;
      state.serviceDesk.deleteServiceDesk.status = ApiStatus.IDLE;
    });
};
